import React from 'react';
import { ArrowLeft } from 'lucide-react';
import MarketingHeader from '../../components/MarketingHeader';
import { REQUEST_ACCESS_URL } from '../../config/links';
import LegalFooter from '../../components/LegalFooter';
import useDocumentMeta from '../../hooks/useDocumentMeta';

const UniversityTeamsPage = () => {

  useDocumentMeta({
    title: 'Factory OS for University Competition Teams — The Engineering Operating System',
    description:
      "Free for university competition teams: the full Team tier of the Engineering Operating System at no cost with a .edu email. Last year's decisions, alternatives, and test results are still on the node when this year's team opens the project — no card, no time limit.",
    path: '/for/university-teams',
  });

  return (
    <div className="fos-notebook min-h-screen">
      <MarketingHeader />

      {/* ══════════ HERO ══════════ */}
      <section className="pt-14 sm:pt-24 pb-10 sm:pb-16 px-5">
        <div className="max-w-[1080px] mx-auto">
          <a href="/" className="inline-flex items-center gap-2 no-underline mb-8" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            <ArrowLeft size={14} /> Back to Factory OS
          </a>

          <div className="mb-5">
            <span className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>
              the engineering operating system · free for .edu
            </span>
          </div>

          <h1 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.05, color: 'var(--ink)', letterSpacing: '-0.5px' }}>
            Built for university competition teams.<br />
            <span className="nb-under">Knowledge that survives graduation.</span>
          </h1>

          <p className="mt-5 max-w-[680px]" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.45 }}>
            Every year, your team graduates and the next team starts from a blank page. Factory OS keeps the why on the node — so next year's lead engineer reads what last year's team decided, and starts from a proven base instead of guessing.
          </p>

          <p className="mt-3 max-w-[680px]" style={{ fontFamily: 'var(--script)', color: 'var(--nb-accent)', fontSize: 'var(--t-md)', lineHeight: 1.2 }}>
            The team that compounds knowledge wins. Make the transfer automatic.
          </p>

          <div className="flex flex-wrap gap-3 mt-7">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Get Team free with .edu</a>
            <a href="https://demo.factory-os.com" className="nb-btn nb-btn-ghost">▶ Take the 2-minute tour</a>
          </div>

          <div className="mt-4" style={{ fontFamily: 'var(--mono)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)', letterSpacing: '0.4px' }}>
            .edu email · full Team tier · no card · no time limit
          </div>
        </div>
      </section>

      {/* ══════════ THE TURNOVER PROBLEM ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow">the turnover problem</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              Every season restarts. The team never compounds.
            </h2>
            <p className="mt-3" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)' }}>
              Year 1 learns by failing — and graduates. Year 2 inherits the CAD files but not the decisions. Year 3 hits the same wall Year 1 did. The repo gets bigger; the knowledge doesn't.
            </p>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 list-none p-0 m-0">
            {[
              "Year 1 picked 4130 over Ti — the reason left with the engineer.",
              "Year 2 finds the CAD file, can't find the trade study, picks Ti again.",
              "Last season's failure data lives in a CSV nobody's opened since the competition.",
              "The captain spends October reverse-engineering September's decisions instead of moving forward.",
            ].map((item, i) => (
              <li key={i} className="nb-sketch-thin p-4" style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.4 }}>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--ink-4)', marginRight: 8 }}>✗</span>{item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ══════════ HOW IT FITS ══════════ */}
      <section className="py-12 md:py-[72px] px-5" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[1080px] mx-auto">
          <div className="max-w-[720px] mb-7">
            <div className="nb-eyebrow" style={{ color: 'var(--nb-accent)' }}>how it fits</div>
            <h2 className="mt-2" style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)' }}>
              Next year's team starts where last year's team finished.
            </h2>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 list-none p-0 m-0">
            {[
              ['Decisions on the node', 'Alternatives considered, materials rejected, cost deltas locked in. The why doesn\'t graduate.'],
              ['Carry-forward', "Year 2 opens the next revision and the failure from Year 1 is already cited. No reverse-engineering."],
              ['IADT verification', "Inspection · Analysis · Demonstration · Test — the verification model real engineering orgs use. Your team graduates ready."],
              ['7 phases, hard gates', "You can't skip the work. The system asks for the evidence. Captains love this; freshmen learn from it."],
              ['Team tier is free with .edu', 'Full Team tier — free for every member of your team, carry-forward learning, DFM + quote workflows, team administration. No card. No 30-day timer that fires mid-build.'],
              ['Shop access too', 'Complete a full design cycle on the platform and we\'ll fabricate parts — FDM today, CNC coming online.'],
            ].map(([title, body], i) => (
              <li key={i} className="nb-sketch-thin p-4">
                <div style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-md)', color: 'var(--ink)', marginBottom: 6 }}>{title}</div>
                <div style={{ fontFamily: 'var(--hand)', fontSize: 'var(--t-md)', color: 'var(--ink-2)', lineHeight: 1.4 }}>{body}</div>
              </li>
            ))}
          </ul>

          <div className="nb-sticky mt-7" style={{ transform: 'rotate(-0.5deg)' }}>
            <b>Knowledge transfer, automated.</b><br />
            The team that wins year-over-year is the team that compounds. Factory OS makes that compounding the default — not a Google Doc that gets lost between presidents.
          </div>
        </div>
      </section>

      {/* ══════════ CLOSER ══════════ */}
      <section className="py-12 md:py-[72px] px-5 text-center" style={{ borderTop: '1px dashed var(--ink-4)' }}>
        <div className="max-w-[820px] mx-auto">
          <h2 style={{ fontFamily: 'var(--hand)', fontWeight: 700, fontSize: 'var(--t-lg)', lineHeight: 1.1, color: 'var(--ink)', marginBottom: 14 }}>
            This season, build on last season's record.
          </h2>
          <p className="mb-7" style={{ fontFamily: 'var(--script)', fontSize: 'var(--t-md)', color: 'var(--nb-accent)' }}>
            Free with .edu. No card. No trial timer. Just build.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href={REQUEST_ACCESS_URL} className="nb-btn no-underline text-center">Get Team free with .edu</a>
            <a href="https://demo.factory-os.com" className="nb-btn nb-btn-ghost">▶ Take the 2-minute tour</a>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default UniversityTeamsPage;
